import { Suspense, lazy, useEffect, useState, ReactNode } from 'react';
const Form = lazy(() => import('./Form'));
const FormInput = lazy(() => import('./FormComponents/FormInput'));

import { getPersonalData } from '../services/api';
import { FieldValues, useForm, UseFormReturn } from 'react-hook-form';
import { PersonDataDto } from '../services/generatedApi';

const ssnFormDataDefaultValues = {
  ssn: '',
  firstName: '',
  zipCode: '',
  address: '',
  city: '',
  lastName: '',
  email: '',
  phone: '',
};

export default function FormWithSsnData({
  children,
  form,
  postUrl,
  novalidate,
  setDisableSubmit,
  labelOnBg,
}: {
  children: ReactNode;
  form: UseFormReturn<FieldValues>;
  postUrl: string;
  novalidate?: boolean;
  labelOnBg?: boolean;
  setDisableSubmit(val: boolean): void;
}): JSX.Element {
  const ssnNumberForm = useForm();
  const [formValues, setFormValues] = useState<{
    ssn: string;
    firstName: string;
    lastName: string;
    zipCode: string;
    address: string;
    city: string;
    email: string;
    phone: string;
  }>(ssnFormDataDefaultValues);
  const [disableEdit, setDisableEdit] = useState(false);
  const [disableEditFirstName, setDisableEditFirstName] = useState(false);
  const [disableEditLastName, setDisableEditLastName] = useState(false);
  const [disableEditAddress, setDisableEditAddress] = useState(false);
  const [disableEditZipCode, setDisableEditZipCode] = useState(false);
  const [disableEditCity, setDisableEditCity] = useState(false);

  const [showError, setError] = useState(false);
  const [ssnResult, setSsnResult] = useState<PersonDataDto>();

  function hasAsterisk(obj: Record<string, string>): boolean {
    let includedAsterisk = false;
    for (const key in obj) {
      if (obj[key]?.includes('*')) {
        setDisableSubmit(true);

        if (key === 'firstName') {
          form.setError('fname', {
            type: 'custom',
            message: 'Ange ett giltigt namn',
          });
        } else if (key === 'lastName') {
          form.setError('lname', {
            type: 'custom',
            message: 'Ange ett giltigt namn',
          });
        } else if (key === 'address') {
          form.setError('street-address', {
            type: 'custom',
            message: 'Ange en gilting adress',
          });
        } else if (key === 'zipCode') {
          form.setError('postal-code', {
            type: 'custom',
            message: 'Ange ett giltigt värde',
          });
        } else if (key === 'city') {
          form.setError('cityVisible', {
            type: 'custom',
            message: 'Ange en giltig postort',
          });
        }
        includedAsterisk = true;
      }
    }
    return includedAsterisk;
  }

  function modifyStr(stringItem: string) {
    var str = stringItem;
    var modifiedStr = str.replace(
      /^(.)(.*)(.)$/,
      function (match, firstLetter, middleLetters, lastLetter) {
        var asterisks = '*'.repeat(middleLetters.length);
        return firstLetter + asterisks + lastLetter;
      }
    );
    return modifiedStr;
  }
  async function fetchSsn() {
    if (!formValues.ssn) {
      setError(true);
      return;
    }
    let fetchValues = formValues.ssn.replace('-', '');

    if (fetchValues.length === 10) {
      const values = fetchValues.slice(0, 1);
      if (values == '0' || values == '1') {
        fetchValues = '20' + fetchValues;
      } else {
        fetchValues = '19' + fetchValues;
      }
    }

    const ssnDetailsResp = await getPersonalData(fetchValues);

    if (
      !ssnDetailsResp.address &&
      !ssnDetailsResp.city &&
      !ssnDetailsResp.firstName &&
      !ssnDetailsResp.lastName &&
      !ssnDetailsResp.zipCode
    ) {
      setError(true);
      return;
    } else {
      form.clearErrors();
      setDisableSubmit(false);
      setError(false);
      setDisableEdit(true);
      if (ssnDetailsResp.firstName) {
        setDisableEditFirstName(true);
      }
      if (ssnDetailsResp.lastName) {
        setDisableEditLastName(true);
      }
      if (ssnDetailsResp.address) {
        setDisableEditAddress(true);
      }
      if (ssnDetailsResp.zipCode) {
        setDisableEditZipCode(true);
      }
      if (ssnDetailsResp.city) {
        setDisableEditCity(true);
      }
      setFormValues({ ...formValues, ...ssnDetailsResp });
      setSsnResult(ssnDetailsResp);

      form.setValue('firstname', ssnDetailsResp?.firstName);
      form.setValue('fname', modifyStr(ssnDetailsResp?.firstName));

      form.setValue('lastname', ssnDetailsResp?.lastName);
      form.setValue('lname', modifyStr(ssnDetailsResp?.lastName));

      form.setValue('Address', ssnDetailsResp?.address);
      form.setValue('street-address', modifyStr(ssnDetailsResp?.address));

      form.setValue('city', ssnDetailsResp?.city);
      form.setValue('cityVisible', modifyStr(ssnDetailsResp?.city));

      form.setValue('zipCode', ssnDetailsResp?.zipCode);
      form.setValue('postal-code', modifyStr(ssnDetailsResp?.zipCode));
    }
  }

  function clearValues() {
    form.setValue('firstname', '');
    form.setValue('fname', '');
    formValues.firstName = '';

    form.setValue('lastname', '');
    form.setValue('lname', '');
    formValues.lastName = '';

    form.setValue('Address', '');
    form.setValue('street-address', '');
    formValues.address = '';

    form.setValue('city', '');
    form.setValue('cityVisible', '');
    formValues.city = '';

    form.setValue('zipCode', '');
    form.setValue('postal-code', '');
    formValues.zipCode = '';
  }
  useEffect(() => {
    form.setValue('firstname', formValues?.firstName);
    if (disableEditFirstName) {
      form.setValue('fname', modifyStr(formValues?.firstName));
    } else {
      form.setValue('fname', formValues?.firstName);

      if (formValues?.firstName.indexOf('*') == -1) {
        form.clearErrors('fname');
      }
    }
  }, [formValues?.firstName]);

  useEffect(() => {
    form.setValue('lastname', formValues?.lastName);

    if (disableEditLastName) {
      form.setValue('lname', modifyStr(formValues?.lastName));
    } else {
      form.setValue('lname', formValues?.lastName);

      if (formValues?.lastName.indexOf('*') == -1) {
        form.clearErrors('lname');
      }
    }
  }, [formValues?.lastName]);

  useEffect(() => {
    form.setValue('Address', formValues?.address);

    if (disableEditAddress) {
      form.setValue('street-address', modifyStr(formValues?.address));
    } else {
      form.setValue('street-address', formValues?.address);
      if (formValues?.address.indexOf('*') == -1) {
        form.clearErrors('street-address');
      }
    }
  }, [formValues?.address]);

  useEffect(() => {
    form.setValue('city', formValues?.city);
    if (disableEditCity) {
      form.setValue('cityVisible', modifyStr(formValues?.city));
    } else {
      form.setValue('cityVisible', formValues?.city);
      if (formValues?.city.indexOf('*') == -1) {
        form.clearErrors('cityVisible');
      }
    }
  }, [formValues?.city]);

  useEffect(() => {
    form.setValue('zipCode', formValues?.zipCode);
    if (disableEditZipCode) {
      form.setValue('postal-code', modifyStr(formValues?.zipCode));
    } else {
      form.setValue('postal-code', formValues?.zipCode);
      if (formValues?.zipCode.indexOf('*') == -1) {
        form.clearErrors('postal-code');
      }
    }
  }, [formValues?.zipCode]);

  useEffect(() => {
    if (!disableEdit && !hasAsterisk(formValues)) {
      setDisableSubmit(false);
    }
  }, [formValues]);

  return (
    <>
      <Suspense>
        <Form
          form={ssnNumberForm}
          postUrl={`/PersonData/GetPersonInformation?SSN=${formValues.ssn}`}
          novalidate={novalidate}
        >
          <div className="md:flex md:flex-wrap ssn-form-input">
            <FormInput
              placeholder="Personnummer"
              label="Personnummer (ÅÅÅÅMMDDXXXX)"
              name="ssn"
              type="text"
              controller={{
                value: formValues.ssn,
                setValue: (ssn) => setFormValues({ ...formValues, ssn }),
              }}
              labelOnBg={labelOnBg}
            />
            <div className="relative md:my-3 mt-4 mb-0 md:px-2 md:w-1/2">
              <button
                className="inset-shadow border-1 px-5 py-3 md:py-[.9rem] hover-blue-border border-blue700 rounded-md w-full font-icb text-blue700 transform duration-200 cursor-pointer"
                type="button"
                onClick={fetchSsn}
                disabled={!formValues.ssn}
              >
                Hämta uppgifter automatiskt
              </button>
              {showError === true && (
                <p className="right-0 md:absolute m-0 px-2 pt-2 text-red500 text-tiny">
                  Fyll i ett korrekt personnummer
                </p>
              )}
            </div>
          </div>
        </Form>
      </Suspense>

      <div className="mdplus:mx-2 mt-3 mdplus:mt-0">
        <a
          href="/integritetspolicy"
          target="_blank"
          className="text-blue700 underline"
        >
          Läs om hur vi behandlar dina uppgifter
        </a>
      </div>
      <div className="flex items-center mdplus:mx-2 my-4 mt-5">
        <div className="flex-1 border-grey100 border-t-2 w-1/2"></div>
        <span className="mx-8 font-ic text-grey300">eller</span>
        <div className="flex-1 border-grey100 border-t-2 w-1/2"></div>
      </div>
      {ssnResult && disableEdit === true && (
        <div className="ml-2 pb-2 md:pb-3">
          <button
            className="block md:inline-block md:ml-2 font-icb align-text-top text-blue700 edit-btn"
            onClick={() => {
              setDisableEdit(false);
              setDisableEditFirstName(false);
              setDisableEditLastName(false);
              setDisableEditAddress(false);
              setDisableEditCity(false);
              setDisableEditZipCode(false);
              clearValues();
            }}
          >
            Ta bort och fyll i uppgifterna själv
          </button>
        </div>
      )}
      <Suspense>
        <Form form={form} postUrl={postUrl} novalidate={novalidate}>
          <div className="md:flex md:flex-wrap">
            <FormInput
              placeholder="Förnamn*"
              label="Förnamn*"
              name="fname"
              disabled={!ssnResult?.firstName ? false : disableEdit}
              required
              controller={{
                value: formValues.firstName,
                setValue: (firstName) =>
                  setFormValues({ ...formValues, firstName }),
              }}
              labelOnBg={labelOnBg}
            />

            <FormInput
              placeholder="Efternamn*"
              label="Efternamn*"
              name="lname"
              required
              disabled={!ssnResult?.lastName ? false : disableEdit}
              controller={{
                value: formValues.lastName,
                setValue: (lastName) =>
                  setFormValues({ ...formValues, lastName }),
              }}
              labelOnBg={labelOnBg}
            />
            <FormInput
              placeholder="Gatuadress*"
              label="Gatuadress*"
              name="street-address"
              required
              disabled={!ssnResult?.address ? false : disableEdit}
              controller={{
                value: formValues.address,
                setValue: (address) =>
                  setFormValues({ ...formValues, address }),
              }}
              labelOnBg={labelOnBg}
            />

            <div className="md:flex md:w-1/2">
              <FormInput
                placeholder="Postnummer*"
                label="Postnummer*"
                name="postal-code"
                pattern={/^[0-9 *]{5,6}$/}
                required
                disabled={!ssnResult?.zipCode ? false : disableEdit}
                controller={{
                  value: formValues.zipCode,
                  setValue: (zipCode) =>
                    setFormValues({ ...formValues, zipCode }),
                }}
                labelOnBg={labelOnBg}
              />

              <FormInput
                placeholder="Postort*"
                label="Postort*"
                name="cityVisible"
                pattern={/^[A-Za-zÅÄÖåäö *-]{2,20}$/}
                required
                disabled={!ssnResult?.city ? false : disableEdit}
                controller={{
                  value: formValues.city,
                  setValue: (city) => setFormValues({ ...formValues, city }),
                }}
                labelOnBg={labelOnBg}
              />
              <FormInput
                name="firstname"
                type="hidden"
                placeholder=""
              ></FormInput>
              <FormInput placeholder="" name="lastname" type="hidden" />
              <FormInput placeholder="" name="Address" type="hidden" />
              <FormInput placeholder="" name="zipCode" type="hidden" />
              <FormInput placeholder="" name="city" type="hidden" />
            </div>
            {children}
          </div>
        </Form>
      </Suspense>
    </>
  );
}
